// Watching an OTA happen, from the strings the device publishes.
//
// Every one of these arrives on `<uno>/client`, the same topic as switch states
// — so classification is not decoration, it is what stops a relay report being
// read as progress. The full list is the firmware's OTA_CLAUDE.md §7.
//
// ⚠️ `OTA started by <user>` DOES NOT MEAN THE OTA STARTED. It is published
// unconditionally once the device decides the command was addressed to it —
// after the no-Wi-Fi branch and after a failed URL lookup, on the same code
// path. A device with no network publishes BOTH `OTA skipped: no Wi-Fi` AND
// `OTA started by X`. Only `OTA started: <url>` means a download is running,
// and only `OTA OK -> booting …` means it worked (§9.1).
//
// ⚠️ THE TWO "started" LINES RACE. `OTA started: <url>` comes from the download
// task and `OTA started by X` from the MQTT handler, so they arrive in either
// order. Never infer anything from sequence.
//
// ⚠️ `OTA OK` IS NOT PROOF THE RELEASE CHANGED. If the new image crash-loops,
// the bootloader reverts 60 s after boot — and `OTA OK -> booting ota_1` was
// already published before the reboot. A silent rollback is indistinguishable
// from success here; only asking VERSION afterwards separates them (§9.4). See
// firmwareVersion.js.

import {
  publishMessage, subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';

export const OTA_COMMAND = 'UPDATE CODE';

// From `OTA started:` to `Device Started` on a slow link. §7 budgets 60–180 s
// for the download alone and warns that anything tighter reports false
// failures. There is no device-side overall timeout to lean on — only a 20 s
// per-read one — so the client has to own this.
export const OTA_TIMEOUT_MS = 5 * 60 * 1000;

// How long to wait for ANY word back before concluding the device is not
// listening at all.
//
// ⚠️ NOT OTA_TIMEOUT_MS — that is the DOWNLOAD budget, and the question here is
// a different and much faster one: "did you hear me". A device that is
// connected answers in milliseconds, because the ack is published the moment
// the MQTT handler decides the command was addressed to it — before any of the
// work. Ten seconds is a broker round trip with room to spare, and it matches
// the firmware's own patience: it gives up on the broker and drops to BLE
// within about eight.
export const OTA_SILENCE_MS = 10 * 1000;

export const OTA_SKIPPED = 'skipped';
export const OTA_STARTED = 'started';
export const OTA_ACK = 'ack';
export const OTA_REFUSED = 'refused';
export const OTA_FAILED = 'failed';
export const OTA_OK = 'ok';
export const OTA_REBOOTED = 'rebooted';

/**
 * What does this line from `<uno>/client` mean?
 *
 * -> { kind, text } or null when it is not about an OTA at all.
 *
 * Order matters below: `OTA started by` must be tested BEFORE `OTA started`,
 * or the ack is mistaken for the real thing — which is precisely the trap §9.1
 * describes.
 */
export function classifyOtaLine(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  if (/^OTA\s+started\s+by\b/i.test(s)) return { kind: OTA_ACK, text: s };
  if (/^OTA\s+started\s*:/i.test(s)) return { kind: OTA_STARTED, text: s };
  if (/^OTA\s+skipped\b/i.test(s)) return { kind: OTA_SKIPPED, text: s };
  if (/^OTA\s+refused\b/i.test(s)) return { kind: OTA_REFUSED, text: s };
  if (/^OTA\s+FAILED\b/i.test(s)) return { kind: OTA_FAILED, text: s };
  if (/^OTA\s+OK\b/i.test(s)) return { kind: OTA_OK, text: s };
  // The retained startup packet — the device is back up after the reboot.
  if (/^Device\s+Started\b/i.test(s)) return { kind: OTA_REBOOTED, text: s };
  return null;
}

// Strip the `[uno]…#who` wrapper the status protocol uses. OTA lines are
// published bare, but going through the same unwrap costs nothing and means a
// wrapped one is still understood.
function body(payload) {
  const s = String(payload || '');
  const inner = s.includes(']') ? s.slice(s.indexOf(']') + 1) : s;
  return inner.split('#')[0].trim();
}

export const otaPayload = (uniqueNumber, userId) =>
  `[${uniqueNumber}]${OTA_COMMAND}#${userId}`;

/**
 * Trigger an update and watch it.
 *
 * `onEvent({kind, text})` fires for every OTA line, including the ack — the
 * caller decides what to show, but the classification is done here so no screen
 * has to re-derive §9.1.
 *
 * `onSilence()` fires ONCE, after OTA_SILENCE_MS, if nothing has come back —
 * i.e. the publish went into a device that is not connected, which MQTT cannot
 * report as an error because publishing to nobody is not one.
 *
 * ⚠️ THE ACK COUNTS HERE, and this is the one place in the file where it is
 * worth something. Everywhere else `OTA started by <user>` is treated as
 * near-worthless, because it is published even on the no-Wi-Fi and failed-URL
 * branches and so proves nothing about the OTA. But it is published by the MQTT
 * handler itself, which means it is conclusive proof of the ONLY thing
 * `onSilence` asks: the device received the command. A device that acks and
 * then fails is a device that was listening, and must not be queued.
 *
 * -> stop(), which unsubscribes AND cancels the silence timer. Call it when the
 * dialog closes, or a queue will be written for a screen nobody is looking at.
 *
 * Subscribes BEFORE publishing: the device answers in milliseconds and a
 * subscription taken afterwards loses the first line.
 */
export function startOtaAndWatch(uniqueNumber, userId, onEvent, onSilence) {
  const uno = String(uniqueNumber || '').trim();
  const topic = `${uno}/client`;
  let timer = null;
  const heard = () => { if (timer) { clearTimeout(timer); timer = null; } };
  const off = addMessageListener((t, payload) => {
    if (t !== topic) return;
    const ev = classifyOtaLine(body(payload));
    if (!ev) return;
    // Cancel BEFORE handing the event on: a throw in onEvent must not leave the
    // timer armed and queue an update on a device that just answered.
    heard();
    if (onEvent) onEvent(ev);
  });
  subscribeTopic(topic);
  publishMessage(`${uno}/device`, otaPayload(uno, userId));
  if (onSilence) timer = setTimeout(() => { timer = null; onSilence(); }, OTA_SILENCE_MS);
  return () => { heard(); off(); unsubscribeTopic(topic); };
}

/**
 * Has this run reached a conclusion, and which one?
 *
 * -> 'running' | 'failed' | 'flashed' | 'back'
 *
 * ⚠️ 'flashed' IS NOT 'succeeded'. It means the device accepted the image and
 * rebooted. Whether the release actually changed is a separate question with a
 * separate answer — see the rollback note at the top.
 */
export function otaOutcome(events) {
  const kinds = (events || []).map((e) => e.kind);
  if (kinds.includes(OTA_REBOOTED) && kinds.includes(OTA_OK)) return 'back';
  if (kinds.includes(OTA_OK)) return 'flashed';
  if (kinds.some((k) => k === OTA_FAILED || k === OTA_SKIPPED || k === OTA_REFUSED)) return 'failed';
  return 'running';
}
